import React, { useState, useEffect, useContext } from "react";
import "./NuevoProducto.css";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Preloader from "../../components/Preloader";
import { ProductService } from "../../services/ProductService";
import { CategoriaService } from "../../services/CategoriaService";
import { AuthContext } from "../../context/AuthContext";

const ActualizarProducto = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const { id } = useParams();
  const [form, setForm] = useState({
    codigo: "",
    nombre: "",
    categoria: "",
    descripcion: "",
    precio: "",
    estado: "ACTIVO",
  });
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [codigoBloqueado, setCodigoBloqueado] = useState(false);
  const [error, setError] = useState("");
  const [errores, setErrores] = useState({});
  const [success, setSuccess] = useState("");

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const productData = await ProductService.fetchProductoPorId({ token, id });

        if (productData?.estado !== 'ACTIVO') {
          navigate('/admin/productos');
          return;
        }

        setForm({
          codigo: productData.codigo,
          nombre: productData.nombre,
          categoria: productData.categoria_id,
          descripcion: productData.descripcion,
          precio: productData.precio,
          estado: productData.estado,
        });

        setCodigoBloqueado(!!productData.codigo);

        setLoading(false);
      } catch (error) {
        setLoading(false);
        if (error.response?.status === 404 && error.response?.data?.error === 'Producto no encontrado') {
          navigate('/admin/productos');
          return;
        }
        console.error("Error al obtener la venta:", error);
      }
    };

    if (id) {
      cargarCategorias();
      fetchProduct();
    }
  }, [id, token]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleBackToList = () => {
    navigate("/admin/productos"); // ajusta según tu ruta de listado
  };

  const cargarCategorias = async () => {
    try {
      const data = await CategoriaService.getCategorias(token);
      setCategorias(data);
    } catch (err) {
      console.error("Error al cargar categorías:", err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errores = validarFormulario();

    if (Object.keys(errores).length > 0) {
      setErrores(errores);
      return;
    }
    setLoading(true);
    try {
      await ProductService.editarProducto({ token, id, form });
      toast.success("Producto actualizado correctamente");
      navigate('/admin/productos');
    } catch (err) {
      if (err.response && err.response.status === 409) {
        // Mostrar el mensaje que viene del backend
        toast.error(err.response.data.error);
      } else {
        toast.error("Error al registrar el producto.");
      }
    } finally {
      setLoading(false);
    }
  };

  const validarFormulario = () => {
    const errores = {};

    if (!form.codigo.trim()) {
      errores.codigo = "El código es obligatorio";
    }

    if (!form.nombre.trim()) {
      errores.nombre = "El nombre es obligatorio";
    }

    if (!form.categoria) {
      errores.categoria = "La categoría es obligatoria";
    }

    if (!form.descripcion.trim()) {
      errores.descripcion = "La descripción es obligatoria";
    }

    if (!form.precio || isNaN(form.precio) || Number(form.precio) <= 0) {
      errores.precio = "El precio debe ser un número mayor a 0";
    }

    return errores;
  };

  return (
    <div className="ventas-admin-content">
      {loading && <Preloader />}
      <div className="breadcrumb-container">
        <span className="breadcrumb-link" onClick={handleBackToList}>
          ← Listado de Productos
        </span>
        <span className="breadcrumb-current"> / Actualizar Producto</span>
      </div>
      <div className="dashboard-card nuevo-producto-card">
        <span className="nueva-venta-title"> Actualizar Producto</span>
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}
        <form className="nuevo-producto-form" onSubmit={handleSubmit}>
          <div className="form-section">
            <h3 className="section-title"> Información Básica</h3>
            <div className="form-grid">
              <div className="form-group large">
                <label htmlFor="codigo">Código del Producto *</label>
                <input
                  type="text"
                  name="codigo"
                  value={form.codigo}
                  onChange={handleChange}
                  placeholder="Ingrese código"
                  readOnly={codigoBloqueado}   // solo se bloquea si ya existía en la BD
                />
                {errores.codigo && (
                  <span className="error">{errores.codigo}</span>
                )}
              </div>

              <div className="form-group large">
                <label htmlFor="nombre">Nombre del Producto *</label>
                <input
                  type="text"
                  name="nombre"
                  value={form.nombre}
                  onChange={handleChange}
                  placeholder="Ej: Internet 50 Mbps, Cable Premium, Combo Internet + Cable"
                />
                {errores.nombre && (
                  <span className="error">{errores.nombre}</span>
                )}
              </div>
              <div className="form-group large">
                <label htmlFor="categoria">Categoría *</label>
                <select
                  name="categoria"
                  value={form.categoria}
                  onChange={handleChange}
                >
                  <option value="">Seleccione una categoría</option>
                  {categorias.map((categoria) => (
                    <option key={categoria.id} value={categoria.id}>
                      {categoria.nombre}
                    </option>
                  ))}
                </select>
                {errores.categoria && (
                  <span className="error">{errores.categoria}</span>
                )}
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3 className="section-title">Descripción del Producto</h3>
            <div className="form-group full-width">
              <label htmlFor="descripcion">Descripción Detallada *</label>
              <textarea
                name="descripcion"
                value={form.descripcion}
                onChange={handleChange}
                placeholder="Describa detalladamente el producto o servicio. Incluya características, beneficios, especificaciones técnicas, cobertura, velocidad, canales, etc."
                rows="6"
              />
              {errores.descripcion && (
                <span className="error">{errores.descripcion}</span>
              )}
            </div>
          </div>

          <div className="form-section">
            <h3 className="section-title"> Información Comercial</h3>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="precio">Precio (S/) *</label>
                <input
                  type="number"
                  name="precio"
                  value={form.precio}
                  onChange={handleChange}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                />
                {errores.precio && (
                  <span className="error">{errores.precio}</span>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="estado">Estado del Producto *</label>
                <select
                  name="estado"
                  value={form.estado}
                  onChange={handleChange}
                >
                  <option value="ACTIVO">
                    {" "}
                    ACTIVO - Disponible para venta
                  </option>
                  <option value="BAJA">
                    {" "}
                    BAJA - No disponible temporalmente
                  </option>
                </select>
                {errores.estado && (
                  <span className="error">{errores.estado}</span>
                )}
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button
              className="btn btn-secondary"
              type="button"
              onClick={() =>
                setForm({
                  nombre: "",
                  categoria: "",
                  descripcion: "",
                  precio: "",
                  estado: "ACTIVO",
                })
              }
            >
              Limpiar Formulario
            </button>
            <button
              className="btn btn-primary"
              type="submit"
              disabled={loading}
            >
              {loading ? "Actualizando..." : " Actualizar Producto"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ActualizarProducto;
